import produce from 'immer'

import {
  DeliveryForecast,
  DeliveryForecastItem,
  WebEdiDeliveryForecastDto,
} from '../../../../data/models'

interface ResponsePeriod {
  Start: Date
  End: Date
}

interface Item extends Omit<DeliveryForecastItem, 'ModificationDate'> {
  ModificationDate: Date | null
}

interface Payload
  extends Omit<
    DeliveryForecast,
    | 'CurrentReleaseDate'
    | 'PreviousReleaseDate'
    | 'ResponsePeriod'
    | 'ConfirmationDate'
    | 'DeliveryForecastItem'
  > {
  CurrentReleaseDate: Date
  PreviousReleaseDate: Date
  ResponsePeriod: ResponsePeriod
  ConfirmationDate: Date
  Item: Item
}

interface ConvertedResponse
  extends Omit<
    WebEdiDeliveryForecastDto<DeliveryForecast>,
    'CreationDate' | 'Payload'
  > {
  CreationDate: Date
  Payload: Payload
}

/**
 * Converts a list of WebEdiDeliveryForecastDto<DeliveryForecast> responses into a list of converted response objects.
 *
 * This function uses the Immer library to create a new array of converted response objects from
 * the provided WebEdiDeliveryForecastDto<DeliveryForecast> array. The conversion process involves:
 *
 * 1. Transforming date strings into Date objects for easier filtering and sorting in tables.
 * 2. Moving the first item in the `Items` array to the `Item` property because a table cannot filter,
 *    sort, or display nested arrays effectively.
 *
 * @param {WebEdiDeliveryForecastDto<DeliveryForecast>[] | undefined} responses - The list of responses to be converted.
 * @returns {ConvertedResponse[]} - The list of converted responses.
 */
export function convertDeliveryForecast(
  responses: WebEdiDeliveryForecastDto<DeliveryForecast>[] | undefined,
) {
  // transforms date strings in date objects so it can be filtered in tables
  const convertedResponses = produce(
    responses,
    (draft: ConvertedResponse[]) => {
      draft?.forEach(response => {
        // CreationDate
        response.CreationDate = new Date(response.CreationDate)

        // CurrentReleaseDate
        response.Payload.CurrentReleaseDate = new Date(
          response.Payload.CurrentReleaseDate,
        )

        // PreviousReleaseDate
        response.Payload.PreviousReleaseDate = new Date(
          response.Payload.PreviousReleaseDate,
        )

        // ResponsePeriod
        if (response.Payload.ResponsePeriod) {
          response.Payload.ResponsePeriod.Start = new Date(
            response.Payload.ResponsePeriod.Start,
          )
          response.Payload.ResponsePeriod.End = new Date(
            response.Payload.ResponsePeriod.End,
          )
        }

        // ConfirmationDate
        response.Payload.ConfirmationDate = new Date(
          response.Payload.ConfirmationDate,
        )

        // Item
        // The items are moved to item because a table can't filter, sort or
        // display another array inside an array. So we get the first item in
        // the array "items" and place that in the item as an object.
        if (response.Payload.Items && response.Payload.Items.length > 0) {
          const firstItem = response.Payload.Items[0]

          response.Payload.Item = {
            ...firstItem,
            ModificationDate: firstItem.ModificationDate
              ? new Date(firstItem.ModificationDate)
              : null,
          }
        }
      })
    },
  )

  return convertedResponses
}
